import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { initChat, updateUserData, isChatActive, showChat } from '@sv-support-sdk/chat-interface';

import { useAppDispatch, useAppSelector } from './store/hooks';
import { getUser } from './store/actions/user';
import {
  selectUserEmail,
  selectUserId,
  selectUserLoaded,
  userAuthorizedSelector,
} from './store/selectors/user';
import ticketsActions from './store/actions/tickets';
import { selectTicketsHash } from './store/selectors/tickets';
import sockets, { connection } from './sockets';
import { chatRenderedSelector } from './store/selectors/appState';
import { setChatRendered } from './store/slices/appState';

import ErrorFallback from './containers/ErrorFallback';
import PageHeader from './components/Layout/Page/PageHeader';
import Header from './components/Navigation/Header';
import Sidebar from './components/Navigation/Sidebar';
import PageSidebar from './components/Layout/Page/PageSidebar';
import Page from './components/Layout/Page';
import MainAppContent from './MainAppContent';
import PageLoader from './components/Feedback/Loader/PageLoader';
import MobileHeader from './components/Navigation/MobileHeader';
import MobileMenu from './components/Navigation/MobileMenu';
import OverlayActions from './components/Layout/OverlayActions';
import ChatContainer from './components/Layout/ChatContainer';

import { URL_VERSION_CHAT } from './config';
import { WORKSPACE_STORAGE_NAME, WORKSPACE_SEARCH_PARAM } from './constants/workspace';
import useMobileDetect from './hooks/useMobileDetect';
import { onChatClose } from './store/actions/chats';
import { CONTACT_US_MODAL } from './constants/modals';
import { modalActions } from './store/slices/modals';
import { selectContactUsModal } from './store/selectors/modals';

import('./assets/svg/sprite.svg');

const MainApp = () => {
  const [isMenuMobileOpened, setIsMobileMenuOpened] = useState(false);
  const [contactUsOverlayVisible, setContactUsOverlayVisible] = useState(false);
  const [isChatButtonVisible, setIsChatButtonVisible] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const hash = useAppSelector(selectTicketsHash);
  const dispatch = useAppDispatch();
  const isAuthorized = useAppSelector(userAuthorizedSelector);
  const userId = useAppSelector(selectUserId);
  const userEmail = useAppSelector(selectUserEmail);
  const isUserLoaded = useAppSelector(selectUserLoaded);
  const isChatRendered = useAppSelector(chatRenderedSelector);
  const isMobile = useMobileDetect();
  const contactUsModal = useAppSelector(selectContactUsModal);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpened((state) => !state);
  };

  const hideOverlayAndShowChatButton = () => {
    setIsChatButtonVisible(true);
    setContactUsOverlayVisible(false);
  };

  const onChatClick = () => {
    showChat();
    hideOverlayAndShowChatButton();
  };

  const onContactUsClick = () => {
    if (isChatActive()) {
      showChat();
    } else {
      setContactUsOverlayVisible(true);
      setIsChatButtonVisible(false);
    }
  };

  const workspaceId = useMemo(() => {
    return searchParams.get(WORKSPACE_SEARCH_PARAM);
  }, [searchParams]);

  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  useEffect(() => {
    if (hash && userId) {
      dispatch(ticketsActions.connectToSocketAndSubscribe(userId, hash, sockets, 'workflow'));
    }

    return () => {
      connection?.leaveAllChannels();
    };
  }, [hash, userId, dispatch]);

  useEffect(() => {
    if (isAuthorized) {
      dispatch(ticketsActions.fetchUnreadTickets());
    }
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    if (workspaceId) {
      sessionStorage.setItem(WORKSPACE_STORAGE_NAME, workspaceId);
      setSearchParams({});

      if (isChatRendered) {
        updateUserData({
          id: userId,
          email: userEmail,
          workspaceId,
        });
      }
    }
  }, [workspaceId, isChatRendered, userId, userEmail]);

  useEffect(() => {
    if (!isUserLoaded) return;

    const workspaceId = sessionStorage.getItem(WORKSPACE_STORAGE_NAME);

    initChat({
      url: URL_VERSION_CHAT,
      containerId: 'chat-root',
      user: {
        id: userId,
        email: userEmail,
        workspaceId,
      },
      onChatRender: () => dispatch(setChatRendered(true)),
      onChatClose: () => dispatch(onChatClose()),
      onChatToggle: (state) => {
        if (!state) {
          hideOverlayAndShowChatButton();
        }
      },
      options: {
        hideButton: isMobile,
      },
    });
  }, [isUserLoaded, userId, userEmail, dispatch, isMobile]);

  useEffect(() => {
    if (contactUsModal) {
      hideOverlayAndShowChatButton();
    }
  }, [contactUsModal]);

  if (!isUserLoaded) {
    return <PageLoader />;
  }

  if (isMobile) {
    return (
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <Page isMobile={isMobile}>
          <MobileHeader isMenuOpened={isMenuMobileOpened} toggleMenu={toggleMobileMenu} />
          {isMenuMobileOpened && <MobileMenu onMenuClick={() => setIsMobileMenuOpened(false)} />}
          <MainAppContent />
          {contactUsOverlayVisible && (
            <OverlayActions
              onBlankSpaceClick={hideOverlayAndShowChatButton}
              onChatClick={onChatClick}
              onTicketClick={() => dispatch(modalActions.openModal({ name: CONTACT_US_MODAL }))}
            />
          )}
          <ChatContainer onButtonClick={onContactUsClick} isButtonVisible={isChatButtonVisible} />
        </Page>
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <Page>
        <PageHeader>
          <Header />
        </PageHeader>
        <PageSidebar>
          <Sidebar />
        </PageSidebar>
        <MainAppContent />
      </Page>
    </Sentry.ErrorBoundary>
  );
};

export default MainApp;
